import { useState, useEffect, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import { getContactForCustomer } from '../../services/settingService';
// @ts-ignore
import { getHeroForCustomer } from '../../services/heroService';
// @ts-ignore
import { useAutoSEO } from '../../hooks/useSEOFromRoute';
import { useServices } from '../../contexts/ServicesContext';
import { submitContact } from '../../services/contactService';

interface HeroSection {
  id: string;
  page: string;
  badge?: {
    text: string;
    icon: string;
  };
  heading: string;
  subheading?: string;
  description?: string;
  backgroundImage?: string;
  buttons: Array<{
    text: string;
    url: string;
    actionType: 'modal' | 'url' | 'scroll';
    style: 'primary' | 'secondary' | 'outline';
    order: number;
    isVisible: boolean;
  }>;
  isActive: boolean;
}

const ContactPage = () => {
  useAutoSEO();
  const { services, solutions, academy } = useServices();

  const serviceOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    solutions.forEach((s) => opts.push({ value: s.slug, label: s.name }));
    services.forEach((s) => opts.push({ value: s.slug, label: s.name }));
    if (academy) opts.push({ value: academy.slug, label: academy.name });
    return opts;
  }, [services, solutions, academy]);

  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [contactData, setContactData] = useState<any>(null);
  const [heroSection, setHeroSection] = useState<HeroSection | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceSlug: '',
    serviceName: '',
    message: '',
    website: '', // honeypot - bot trap
  });
  const [submitting, setSubmitting] = useState(false);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const serviceDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target as Node)) {
        setServiceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Section visibility states
  const [visibleSections, setVisibleSections] = useState({
    contact: false,
    faq: false,
    map: false,
  });

  // Refs for scroll animations
  const contactRef = useRef<HTMLElement>(null);
  const faqRef = useRef<HTMLElement>(null);
  const mapRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactResponse, heroResponse] = await Promise.all([
          getContactForCustomer(),
          getHeroForCustomer('contact')
        ]);
        
        if (contactResponse.success && contactResponse.data) {
          setContactData(contactResponse.data);
        }
        
        if (heroResponse.success && heroResponse.data) {
          const heroData = {
            ...heroResponse.data,
            isActive: heroResponse.data.isActive !== undefined ? heroResponse.data.isActive : true,
            buttons: heroResponse.data.buttons || []
          };
          setHeroSection(heroData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  // Trigger animation on mount
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: "0px 0px -100px 0px",
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute("data-section");
          if (sectionName) {
            setVisibleSections((prev) => ({ ...prev, [sectionName]: true }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    const refs = [
      { ref: contactRef, name: "contact" },
      { ref: faqRef, name: "faq" },
      { ref: mapRef, name: "map" },
    ];

    refs.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  const validateEmail = (email: string) => {
    const trimmed = email.trim().toLowerCase();
    if (trimmed.length < 6 || trimmed.length > 254) return false;
    const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    return re.test(trimmed);
  };

  const validateFullName = (name: string) => {
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 200) return false;
    const nameRe = /^[a-zA-ZÀÁÂÃÈÉÊÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêìíòóôõùúăđĩũơƯĂÀÁẢÃẠÂẦẤẪẨẬẰẮẲẴẶÈÉẺẼẸÊỀẾỂỄỆÌÍỈĨỊÒÓỎÕỌÔỒỐỔỖỘỜỚỞỠỢÙÚỦŨỤƯỪỨỬỮỰỲÝỶỸỴăàáảãạâầấẫẩậằắẳẵặèéẻẽẹêềếểễệìíỉĩịòóỏõọôồốổỗộờớởỡợùúủũụưừứửữựỳýỷỹỵ\s]+$/;
    return nameRe.test(trimmed);
  };

  const validatePhone = (phone: string) => {
    const trimmed = phone.trim().replace(/\s/g, '');
    if (trimmed.length === 0) return true;
    if (trimmed.startsWith('+84')) return /^\+84[35789][0-9]{8,9}$/.test(trimmed);
    return /^0[35789][0-9]{8,9}$/.test(trimmed);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.website) return; // honeypot - bot trap
    if (!formData.name || !validateFullName(formData.name)) {
      toast.error('Họ tên chỉ được chứa chữ cái và khoảng trắng, không có số hoặc ký tự đặc biệt (2-200 ký tự)');
      return;
    }
    if (!formData.email || !validateEmail(formData.email)) {
      toast.error('Email không hợp lệ');
      return;
    }
    if (!formData.message || formData.message.trim().length < 10) {
      toast.error('Nội dung phải có ít nhất 10 ký tự');
      return;
    }
    if (formData.phone && !validatePhone(formData.phone)) {
      toast.error('Số điện thoại phải là số Việt Nam hợp lệ (10-11 số, bắt đầu bằng 0)');
      return;
    }
    if (!formData.serviceSlug) {
      toast.error('Vui lòng chọn dịch vụ quan tâm');
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone?.trim() || undefined,
        serviceSlug: formData.serviceSlug || undefined,
        serviceName: formData.serviceName || undefined,
        message: formData.message.trim(),
        website: formData.website || undefined,
      });
      toast.success('Cảm ơn bạn đã liên hệ! Chúng tôi sẽ phản hồi trong vòng 24 giờ.');
      setFormData({ name: '', email: '', phone: '', serviceSlug: '', serviceName: '', message: '', website: '' });
    } catch (err: any) {
      toast.error(err?.message || 'Có lỗi xảy ra. Vui lòng thử lại sau.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'serviceSlug') {
      const opt = serviceOptions.find((o) => o.value === value);
      setFormData((prev) => ({ ...prev, serviceSlug: value, serviceName: opt?.label || '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqData = [
    {
      question: "Thời gian hoàn thành dự án thường là bao lâu?",
      answer: "Thời gian phụ thuộc vào độ phức tạp của dự án. Website đơn giản: 1-2 tuần, ứng dụng mobile: 1-3 tháng, hệ thống IoT: 2-4 tháng. Chúng tôi sẽ đưa ra timeline cụ thể sau khi phân tích yêu cầu."
    },
    {
      question: "Chi phí phát triển được tính như thế nào?",
      answer: "Chi phí được tính dựa trên độ phức tạp, tính năng và thời gian phát triển. Chúng tôi cung cấp báo giá minh bạch, không phát sinh chi phí ẩn. Liên hệ để nhận báo giá chi tiết cho dự án của bạn."
    },
    {
      question: "Có hỗ trợ bảo hành sau khi hoàn thành không?",
      answer: "Có, chúng tôi cung cấp bảo hành 6-12 tháng tùy theo loại dự án. Bao gồm sửa lỗi, cập nhật bảo mật và hỗ trợ kỹ thuật. Ngoài ra còn có gói bảo trì dài hạn cho khách hàng có nhu cầu."
    },
    {
      question: "Có thể theo dõi tiến độ dự án như thế nào?",
      answer: "Chúng tôi cập nhật tiến độ hàng tuần qua email và họp online. Khách hàng có thể xem demo, góp ý và theo dõi từng giai đoạn phát triển một cách minh bạch."
    }
  ];


  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      {heroSection && (heroSection.isActive !== false) ? (
        <section className="relative py-24 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-[#00c897] rounded-full blur-3xl animate-pulse"></div>
            <div
              className="absolute bottom-20 right-10 w-96 h-96 bg-[#00c897] rounded-full blur-3xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div
              className={`text-center transition-all duration-1000 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              {/* Badge */}
              {heroSection.badge?.text && (
                <div className="inline-block px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm">
                  {heroSection.badge.icon && <i className={`${heroSection.badge.icon} mr-2`}></i>}
                  <span className="text-[#00c897] text-sm font-semibold">
                    {heroSection.badge.text}
                  </span>
                </div>
              )}
              {!heroSection.badge?.text && (
                <div className="inline-block px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm">
                  <span className="text-[#00c897] text-sm font-semibold">
                    Hỗ trợ 24/7
                  </span>
                </div>
              )}
              
              <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
                {heroSection.heading}
              </h1>
              
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                {heroSection.description || heroSection.subheading || "Sẵn sàng hỗ trợ bạn 24/7. Hãy chia sẻ ý tưởng và nhận tư vấn miễn phí"}
              </p>
            </div>
          </div>

          {/* Wave Decoration */}
          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg
              className="w-full h-16"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
            >
              <path
                d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z"
                fill="white"
              ></path>
            </svg>
          </div>
        </section>
      ) : (
        // Fallback hero section if no hero data or not active
        <section className="relative py-24 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-[#00c897] rounded-full blur-3xl animate-pulse"></div>
            <div
              className="absolute bottom-20 right-10 w-96 h-96 bg-[#00c897] rounded-full blur-3xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div
              className={`text-center transition-all duration-1000 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              <div className="inline-block px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm">
                <span className="text-[#00c897] text-sm font-semibold">
                  Hỗ trợ 24/7
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
                Liên Hệ Với Chúng Tôi
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Sẵn sàng hỗ trợ bạn 24/7. Hãy chia sẻ ý tưởng và nhận tư vấn miễn phí
              </p>
            </div>
          </div>

          {/* Wave Decoration */}
          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg
              className="w-full h-16"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
            >
              <path
                d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z"
                fill="white"
              ></path>
            </svg>
          </div>
        </section>
      )}

      {/* Contact Info & Form */}
      <section ref={contactRef} data-section="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div className={`transition-all duration-1000 ${
              visibleSections.contact
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-12"
            }`}>
              <h2 className="text-4xl font-bold text-[#333333] mb-8">
                THÔNG TIN LIÊN HỆ
              </h2>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                Chúng tôi luôn sẵn sàng lắng nghe và hỗ trợ bạn. Hãy liên hệ qua bất kỳ kênh nào thuận tiện nhất.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2A4B7C] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#333333] mb-2">Địa chỉ</h3>
                    <p className="text-gray-600">
                      {contactData?.address}<br />
                      Hỗ trợ khách hàng toàn quốc
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00C897] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#333333] mb-2">Điện thoại</h3>
                    <p className="text-gray-600">
                      {contactData?.phone || '+84 xxx xxx xxx'}<br />
                      Hotline: 24/7
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2A4B7C] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#333333] mb-2">Email</h3>
                    <p className="text-gray-600">
                      {contactData?.emails && contactData.emails.length > 0 ? (
                        <>
                          {contactData.emails.map((email: any, index: number) => (
                            <span key={email._id || index}>
                              {email.label}: {email.address}
                              {index < contactData.emails.length - 1 && <br />}
                            </span>
                          ))}
                        </>
                      ) : (
                        <>
                          info@stackway.tech<br />
                          support@stackway.tech
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00C897] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#333333] mb-2">Giờ làm việc</h3>
                    <p className="text-gray-600">
                      {contactData?.workingHours && Array.isArray(contactData.workingHours) ? (
                        <>
                          {contactData.workingHours.map((hour: string, index: number) => (
                            <span key={index}>
                              {hour}
                              {index < contactData.workingHours.length - 1 && <br />}
                            </span>
                          ))}
                        </>
                      ) : (
                        <>
                          T2-T6: 8:00-18:00<br />
                          Thứ 7: 8:00 - 12:00<br />
                          Chủ nhật: Nghỉ
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-12">
                <h3 className="text-xl font-bold text-[#333333] mb-6">Kết nối với chúng tôi</h3>
                <div className="flex gap-4">
                  {contactData?.social?.facebook ? (
                    <a 
                      href={contactData.social.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center hover:bg-[#00C897] transition-colors cursor-pointer"
                    >
                      <i className="ri-facebook-fill text-white text-xl"></i>
                    </a>
                  ) : (
                    <div className="w-12 h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center">
                      <i className="ri-facebook-fill text-white text-xl"></i>
                    </div>
                  )}
                  {contactData?.social?.zalo ? (
                    <a 
                      href={contactData.social.zalo} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center hover:bg-[#00C897] transition-colors cursor-pointer"
                    >
                      <span className="text-white text-xl font-bold">Z</span>
                    </a>
                  ) : (
                    <div className="w-12 h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center">
                      <span className="text-white text-xl font-bold">Z</span>
                    </div>
                  )}
                  {contactData?.social?.linkedin ? (
                    <a 
                      href={contactData.social.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center hover:bg-[#00C897] transition-colors cursor-pointer"
                    >
                      <i className="ri-linkedin-fill text-white text-xl"></i>
                    </a>
                  ) : (
                    <div className="w-12 h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center">
                      <i className="ri-linkedin-fill text-white text-xl"></i>
                    </div>
                  )}
                  {contactData?.social?.youtube ? (
                    <a 
                      href={contactData.social.youtube} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center hover:bg-[#00C897] transition-colors cursor-pointer"
                    >
                      <i className="ri-youtube-fill text-white text-xl"></i>
                    </a>
                  ) : (
                    <div className="w-12 h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center">
                      <i className="ri-youtube-fill text-white text-xl"></i>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className={`transition-all duration-1000 delay-200 ${
              visibleSections.contact
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-12"
            }`}>
              <div className="bg-[#F5F7FA] rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-[#333333] mb-8">
                  GỬI YÊU CẦU TƯ VẤN
                </h2>
                
                <form onSubmit={handleSubmit} id="contact-form" className="scroll-mt-24">
                  <div
                    aria-hidden="true"
                    className="absolute -left-[9999px] w-px h-px overflow-hidden"
                  >
                    <label htmlFor="website-field">Website</label>
                    <input
                      tabIndex={-1}
                      autoComplete="off"
                      type="text"
                      name="website"
                      id="website-field"
                      value={formData.website}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Họ tên *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm bg-white"
                          placeholder="Nhập họ tên của bạn"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm bg-white"
                          placeholder="email@example.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Số điện thoại
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm bg-white"
                        placeholder="VD: 0912345678"
                      />
                    </div>
                    
                    <div ref={serviceDropdownRef} className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dịch vụ quan tâm *
                      </label>
                      <button
                        type="button"
                        onClick={() => setServiceDropdownOpen((v) => !v)}
                        className="w-full min-w-0 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm bg-white text-left flex items-center justify-between cursor-pointer bg-no-repeat bg-[right_0.75rem_center] bg-[length:1.25rem]"
                        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")" }}
                      >
                        <span className={formData.serviceName ? 'text-gray-900' : 'text-gray-500'}>
                          {formData.serviceName || 'Chọn dịch vụ'}
                        </span>
                      </button>
                      {serviceDropdownOpen && (
                        <ul
                          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto"
                          role="listbox"
                        >
                          <li
                            role="option"
                            className="px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setFormData((p) => ({ ...p, serviceSlug: '', serviceName: '' }));
                              setServiceDropdownOpen(false);
                            }}
                          >
                            Chọn dịch vụ
                          </li>
                          {serviceOptions.map((opt) => (
                            <li
                              key={opt.value}
                              role="option"
                              className={`px-4 py-2.5 text-sm cursor-pointer ${
                                formData.serviceSlug === opt.value
                                  ? 'bg-[#2A4B7C]/10 text-[#2A4B7C] font-medium'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                              onClick={() => {
                                setFormData((p) => ({ ...p, serviceSlug: opt.value, serviceName: opt.label }));
                                setServiceDropdownOpen(false);
                              }}
                            >
                              {opt.label}
                            </li>
                          ))}
                        </ul>
                      )}
                      <input
                        type="hidden"
                        name="serviceSlug"
                        value={formData.serviceSlug}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nội dung chi tiết *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={6}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm resize-none bg-white"
                        placeholder="Mô tả chi tiết về dự án, yêu cầu kỹ thuật, timeline mong muốn..."
                        required
                      />
                      <div className="text-right text-xs text-gray-500 mt-1">
                        {formData.message.length}/500
                      </div>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#00C897] hover:bg-[#00C897]/90 disabled:opacity-70 disabled:cursor-not-allowed text-white py-4 rounded-lg font-semibold text-lg transition-colors mt-8 whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? 'Đang gửi...' : 'GỬI YÊU CẦU TƯ VẤN'}
                  </button>
                  
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    Chúng tôi sẽ phản hồi trong vòng 24 giờ
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} data-section="faq" className="py-20 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className={`text-4xl md:text-5xl font-bold text-center text-[#333333] mb-16 transition-all duration-1000 ${
            visibleSections.faq
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-12"
          }`}>
            CÂU HỎI THƯỜNG GẶP
          </h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-700 ${
                  visibleSections.faq
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <h3 className="text-xl font-bold text-[#333333] pr-4">
                    {faq.question}
                  </h3>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-${openFAQ === index ? 'subtract' : 'add'}-line text-2xl text-[#2A4B7C] transition-transform duration-200`}></i>
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section ref={mapRef} data-section="map" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className={`text-4xl font-bold text-center text-[#333333] mb-12 transition-all duration-1000 ${
            visibleSections.map
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-12"
          }`}>
            VỊ TRÍ CỦA CHÚNG TÔI
          </h2>
          <div className={`bg-gray-200 rounded-xl overflow-hidden h-96 transition-all duration-1000 delay-200 ${
            visibleSections.map
              ? "opacity-100 scale-100"
              : "opacity-0 scale-95"
          }`}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3919.4326002567447!2d106.69312631533414!3d10.776530192318734!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31752f4b3332a4d9%3A0x6d1b4c0b0b0b0b0b!2sVietnam!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Vị trí StackWay"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
